import React, { useCallback, useId, useMemo, useState } from 'react';
import { getRelativePosition } from 'chart.js/helpers';
import { Line } from 'react-chartjs-2';

const SEVERITY_COLORS = {
  critical: '#991B1B',
  high: '#C75E1A',
  medium: '#D4A017',
  low: '#2563EB'
};

const INCIDENT_TREND_SEVERITY_META = [
  { key: 'critical', label: 'Critical', color: SEVERITY_COLORS.critical },
  { key: 'high', label: 'High', color: SEVERITY_COLORS.high },
  { key: 'medium', label: 'Medium', color: SEVERITY_COLORS.medium },
  { key: 'low', label: 'Low', color: SEVERITY_COLORS.low }
];

/** Overview / chart date-range keys → rolling window in days (client trend chart). */
export const OVERVIEW_DATE_RANGE_DAYS = {
  last_7_days: 7,
  last_1_month: 30,
  last_3_months: 90,
  last_6_months: 182,
  last_year: 365,
};

/** UI range keys → backend `?range=` tokens (see dashboard/web_dashboard/date_range.py). */
export const OVERVIEW_DATE_RANGE_API = {
  last_7_days: '7d',
  last_1_month: '30d',
  last_3_months: '90d',
  last_6_months: '6m',
  last_year: '1y',
  all_time: 'all',
};

export const OVERVIEW_DATE_RANGE_OPTIONS = [
  { value: 'last_7_days', label: '7 days' },
  { value: 'last_1_month', label: '1 month' },
  { value: 'last_3_months', label: '3 months' },
  { value: 'last_6_months', label: '6 months' },
  { value: 'last_year', label: '1 year' },
  { value: 'all_time', label: 'All time' },
];

/** Chart-only filter options (does not affect Overview KPIs / tables). */
export const CHART_DATE_RANGE_OPTIONS = [
  { value: 'last_7_days', label: '7 days' },
  { value: 'last_1_month', label: '1 month' },
  { value: 'all_time', label: 'All time' },
];

export const DEFAULT_OVERVIEW_DATE_RANGE = 'all_time';
export const DEFAULT_CHART_DATE_RANGE = 'last_1_month';

/** Map UI range → backend `range` query value (`all` = no lookback filter). */
export function overviewDateRangeToApiRange(range) {
  const key = String(range || '').trim();
  return OVERVIEW_DATE_RANGE_API[key] || 'all';
}

/** @deprecated Prefer overviewDateRangeToApiRange — kept for client-side day math. */
export function overviewDateRangeToDaysParam(range) {
  const key = String(range || '').trim();
  if (!key || key === 'all_time') return 'all';
  const api = OVERVIEW_DATE_RANGE_API[key];
  if (api && api !== 'all') return api;
  const days = OVERVIEW_DATE_RANGE_DAYS[key];
  return Number.isFinite(days) ? String(days) : 'all';
}


// Match dashboard type system (Poppins / --font-th / label weight).
const CHART_FONT_FAMILY =
  "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
const AXIS_TITLE_FONT = { family: CHART_FONT_FAMILY, size: 13, weight: '600' };
const AXIS_TICK_FONT = { family: CHART_FONT_FAMILY, size: 12, weight: '500' };

function normalizeTrendDateLabel(rawLabel) {
  const label = String(rawLabel || '').trim();
  const match = label.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match) {
    const [, day, month, year] = match;
    return `${year}-${month}-${day}`;
  }
  return label;
}

/** Parse chart incident `date` (ISO or DD-MM-YYYY) to ms for filtering / sorting. */
function parseTrendIncidentDateMs(raw) {
  const label = String(raw ?? '').trim();
  if (!label) return NaN;
  const normalized = normalizeTrendDateLabel(label);
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? NaN : ms;
}

/**
 * Incident trend line chart by severity. Data from API (prop) only.
 * Backend: HistoryDataDisplayView returns { labels, datasets }; we expect it transformed to
 * incidents[] (see transformHistoryToIncidents). Order of datasets: Critical, High, Medium, Low.
 */
const HeatmapChart = ({
  incidents: propIncidents,
  isDarkMode,
  loadError = '',
  onSeverityClick,
}) => {
  const chartFilterId = useId();
  // Chart-local only — never lifts into Overview KPIs / tables.
  const [viewRange, setViewRange] = useState(DEFAULT_CHART_DATE_RANGE);

  const openSeverityRisks = useCallback(
    (severityLabel, dateLabel) => {
      const severity = String(severityLabel || '').trim();
      if (!severity || typeof onSeverityClick !== 'function') return;
      const date = normalizeTrendDateLabel(dateLabel);
      onSeverityClick(severity, date || undefined);
    },
    [onSeverityClick]
  );

  const incidents = useMemo(() => {
    if (Array.isArray(propIncidents) && propIncidents.length > 0) return propIncidents;
    return [];
  }, [propIncidents]);

  const sortedIncidents = useMemo(() => {
    if (!Array.isArray(incidents) || incidents.length === 0) return [];
    return [...incidents].sort((a, b) => {
      const ta = parseTrendIncidentDateMs(a.date);
      const tb = parseTrendIncidentDateMs(b.date);
      if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
      if (Number.isNaN(ta)) return 1;
      if (Number.isNaN(tb)) return -1;
      return ta - tb;
    });
  }, [incidents]);

  const availableSpanDays = useMemo(() => {
    if (!Array.isArray(sortedIncidents) || sortedIncidents.length === 0) return 0;
    let minMs = Infinity;
    let maxMs = -Infinity;
    for (const inc of sortedIncidents) {
      const t = parseTrendIncidentDateMs(inc.date);
      if (Number.isNaN(t)) continue;
      if (t < minMs) minMs = t;
      if (t > maxMs) maxMs = t;
    }
    if (!Number.isFinite(minMs) || !Number.isFinite(maxMs) || maxMs < minMs) return 0;
    return Math.max(1, Math.round((maxMs - minMs) / 86400000) + 1);
  }, [sortedIncidents]);

  const selectedWindowDays = OVERVIEW_DATE_RANGE_DAYS[viewRange];
  const rangeExceedsAvailable =
    Number.isFinite(selectedWindowDays)
    && availableSpanDays > 0
    && selectedWindowDays > availableSpanDays + 3;

  const filteredIncidents = useMemo(() => {
    if (!Array.isArray(sortedIncidents) || sortedIncidents.length === 0) return [];
    if (viewRange === 'all_time') return sortedIncidents;

    const windowDays = OVERVIEW_DATE_RANGE_DAYS[viewRange];
    if (!Number.isFinite(windowDays)) return sortedIncidents;

    // Anchor to "now" so 7 days / 1 month match the Overview picker, not only the
    // latest sparse history point (which can leave the full series visible).
    let latestMs = Date.now();
    for (const inc of sortedIncidents) {
      const t = parseTrendIncidentDateMs(inc.date);
      if (!Number.isNaN(t)) latestMs = Math.max(latestMs, t);
    }
    const cutoffMs = latestMs - windowDays * 86400000;

    return sortedIncidents.filter((inc) => {
      const t = parseTrendIncidentDateMs(inc.date);
      if (Number.isNaN(t)) return false;
      return t >= cutoffMs;
    });
  }, [sortedIncidents, viewRange]);

  const xTickFormat = useMemo(() => {
    const spanDays = OVERVIEW_DATE_RANGE_DAYS[viewRange];
    if (viewRange === 'all_time' || !Number.isFinite(spanDays) || spanDays >= 365) {
      return { month: 'short', year: 'numeric' };
    }
    if (spanDays <= 31) {
      return { month: 'short', day: 'numeric' };
    }
    return { month: 'short', day: 'numeric', year: '2-digit' };
  }, [viewRange]);

  const yAxisConfig = useMemo(() => {
    const allValues = filteredIncidents.flatMap((item) => INCIDENT_TREND_SEVERITY_META.map((sev) => item[sev.key]));
    const maxValue = Math.max(10, ...allValues);
    const yTickStep = 2;
    // Extra tick of headroom so peak points / stroke are not clipped at the chart top.
    // (Explicit `max` disables Chart.js `grace`.)
    const maxY = Math.ceil(maxValue / yTickStep) * yTickStep + yTickStep;
    return { maxY, yTickStep };
  }, [filteredIncidents]);

  const chartData = useMemo(
    () => ({
      labels: filteredIncidents.map((incident) => incident.date),
      datasets: INCIDENT_TREND_SEVERITY_META.map((severity) => ({
        label: severity.label,
        data: filteredIncidents.map((incident) => incident[severity.key]),
        borderColor: severity.color,
        backgroundColor: severity.color,
        fill: false,
        tension: 0.45,
        borderWidth: 3,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: severity.color,
        pointBorderColor: '#0b0b0b',
        pointBorderWidth: 1
      }))
    }),
    [filteredIncidents]
  );

  const axisTitleColor = isDarkMode ? '#d5d5d5' : '#4b4b4b';
  const axisTickColor = isDarkMode ? '#9c9c9c' : '#666666';
  // Light grids must contrast against chrome card fill (#dcdcdc), not blend into it.
  const xGridColor = isDarkMode ? '#1f1f1f' : '#b0b0b0';
  const yGridColor = isDarkMode ? '#2e2e2e' : '#b0b0b0';

  const chartOptions = useMemo(() => {
    const yTickStep = yAxisConfig.yTickStep;
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      // Keep line stroke / points from being cropped at the plot edges.
      clip: false,
      layout: {
        padding: {
          top: 20,
          right: 16,
          bottom: 8,
          left: 8
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      onClick: (event, _elements, chart) => {
        if (!chart || typeof onSeverityClick !== 'function') return;
        // interaction.mode nearest+axis:x returns Critical first when all series share an x —
        // pick the series whose point is closest to the cursor in 2D instead.
        const atIndex = chart.getElementsAtEventForMode(
          event,
          'index',
          { intersect: false },
          true
        );
        if (!atIndex.length) return;
        let best = atIndex[0];
        try {
          const pos = getRelativePosition(event, chart);
          let bestDist = Infinity;
          for (const el of atIndex) {
            const pt = chart.getDatasetMeta(el.datasetIndex)?.data?.[el.index];
            if (!pt || !Number.isFinite(pt.x) || !Number.isFinite(pt.y)) continue;
            const dx = pt.x - pos.x;
            const dy = pt.y - pos.y;
            const dist = dx * dx + dy * dy;
            if (dist < bestDist) {
              bestDist = dist;
              best = el;
            }
          }
        } catch (_) {
          /* fall back to first index hit */
        }
        const label = chart.data?.datasets?.[best.datasetIndex]?.label;
        const dateLabel = chart.data?.labels?.[best.index];
        openSeverityRisks(label, dateLabel);
      },
      onHover: (event, _elements, chart) => {
        const target = event?.native?.target;
        if (!target?.style) return;
        if (typeof onSeverityClick !== 'function' || !chart) {
          target.style.cursor = 'default';
          return;
        }
        const atIndex = chart.getElementsAtEventForMode(
          event,
          'index',
          { intersect: false },
          true
        );
        target.style.cursor = atIndex.length ? 'pointer' : 'default';
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(18, 18, 18, 0.96)',
          borderColor: '#4a4a4a',
          borderWidth: 1,
          cornerRadius: 10,
          padding: 12,
          caretPadding: 10,
          displayColors: true,
          boxPadding: 6,
          usePointStyle: true,
          titleSpacing: 8,
          bodySpacing: 6,
          titleColor: '#ffffff',
          titleFont: {
            size: 12,
            weight: '600'
          },
          bodyColor: '#f2f2f2',
          bodyFont: {
            size: 12
          },
          callbacks: {
            title: (items) => {
              if (!items.length) return '';
              const raw = items[0].label;
              const ms = parseTrendIncidentDateMs(raw);
              if (Number.isNaN(ms)) return `Date: ${raw}`;
              return `Date: ${new Date(ms).toLocaleDateString(undefined, {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}`;
            },
            label: (context) => `${context.dataset.label}: ${context.formattedValue} incidents`,
            labelPointStyle: () => ({
              pointStyle: 'circle',
              rotation: 0
            })
          }
        }
      },
      scales: {
        x: {
          offset: true,
          title: {
            display: true,
            text: 'Date of Incident',
            color: axisTitleColor,
            font: AXIS_TITLE_FONT,
            padding: { top: 12 }
          },
          ticks: {
            color: axisTickColor,
            font: AXIS_TICK_FONT,
            maxRotation: viewRange === 'last_7_days' || viewRange === 'last_1_month' ? 40 : 0,
            minRotation: viewRange === 'last_7_days' || viewRange === 'last_1_month' ? 40 : 0,
            autoSkip: true,
            maxTicksLimit: viewRange === 'last_7_days' ? 8 : viewRange === 'last_1_month' ? 10 : 8,
            padding: 8,
            callback(value) {
              // Category scale: `value` is the label index (do not use tick array index —
              // autoSkip makes those diverge and freezes the axis on wrong labels).
              const raw = this.getLabelForValue(value) ?? chartData.labels[value] ?? '';
              const ms = parseTrendIncidentDateMs(raw);
              if (Number.isNaN(ms)) return raw;
              return new Date(ms).toLocaleDateString(undefined, xTickFormat);
            }
          },
          grid: {
            color: xGridColor,
            drawTicks: false
          }
        },
        y: {
          beginAtZero: true,
          max: yAxisConfig.maxY,
          ticks: {
            stepSize: yTickStep,
            color: axisTickColor,
            font: AXIS_TICK_FONT,
            padding: 10
          },
          title: {
            display: true,
            text: 'Incident Count',
            color: axisTitleColor,
            font: AXIS_TITLE_FONT,
            padding: { top: 4, bottom: 12 }
          },
          grid: {
            color: yGridColor,
            borderDash: [3, 3],
            drawTicks: false
          }
        }
      }
    };
  }, [
    axisTitleColor,
    axisTickColor,
    chartData,
    onSeverityClick,
    openSeverityRisks,
    viewRange,
    xGridColor,
    xTickFormat,
    yAxisConfig.maxY,
    yAxisConfig.yTickStep,
    yGridColor,
  ]);

  const hasSourceIncidents = sortedIncidents.length > 0;
  const showChart = filteredIncidents.length > 0;

  return (
    <div className="heatmap-block">
      <div className="heatmap-section">
        <h3 className="vuln-title">Incident trend by severity</h3>
        <div className="chart-header-row">
          <div className="severity-legend">
            {INCIDENT_TREND_SEVERITY_META.map((item) => (
              typeof onSeverityClick === 'function' ? (
                <button
                  key={item.key}
                  type="button"
                  className="severity-chip severity-chip--button"
                  onClick={() => openSeverityRisks(item.label)}
                  title={`View open ${item.label.toLowerCase()} vulnerabilities`}
                >
                  <span className="severity-chip-dot" style={{ backgroundColor: item.color }} />
                  <span className="severity-chip-label">{item.label}</span>
                </button>
              ) : (
                <span key={item.key} className="severity-chip">
                  <span className="severity-chip-dot" style={{ backgroundColor: item.color }} />
                  <span className="severity-chip-label">{item.label}</span>
                </span>
              )
            ))}
          </div>
          <div className="chart-filter">
            <select
              id={`${chartFilterId}-view`}
              value={viewRange}
              onChange={(event) => setViewRange(event.target.value)}
              className="chart-filter-select"
              aria-label="Incident trend date range"
            >
              {CHART_DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {rangeExceedsAvailable ? (
          <p className="chart-filter-range-note" role="status">
            Showing all available history ({availableSpanDays} day{availableSpanDays === 1 ? '' : 's'}).
            Longer ranges look the same until more trend history is available.
          </p>
        ) : null}
        <div className="heatmap-chart-wrap">
          {showChart ? (
            <div className="heatmap-chart-canvas">
              <Line key={viewRange} data={chartData} options={chartOptions} />
            </div>
          ) : loadError ? (
            <div className="heatmap-empty-state" role="alert">
              {loadError}
            </div>
          ) : hasSourceIncidents ? (
            <div className="heatmap-empty-state" role="status">
              No incident points fall in the selected view. Try a wider range.
            </div>
          ) : (
            <div className="heatmap-empty-state" role="status">
              No incident trend data available for this view yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Converts backend history payload into chart-friendly incidents array.
 * Backend (HistoryDataDisplayView): { labels: string[], datasets: [Critical, High, Medium, Low] }.
 * Each dataset has { label, data: number[] }; data[i] corresponds to labels[i].
 */
export function transformHistoryToIncidents(history) {
  if (!history?.labels?.length || !Array.isArray(history.datasets)) return null;

  const datasetMap = new Map(
    history.datasets.map((dataset) => [String(dataset?.label || '').trim().toLowerCase(), dataset])
  );
  const keys = ['critical', 'high', 'medium', 'low'];

  return history.labels.map((rawDate, i) => {
    const point = { date: normalizeTrendDateLabel(rawDate) };
    keys.forEach((key) => {
      point[key] = datasetMap.get(key)?.data?.[i] ?? 0;
    });
    return point;
  });
}

export default HeatmapChart;
